import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Activity, BookOpen, Calendar, CheckCircle2, Flame, TrendingUp, Cross } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationService, progressService, userService, lapseService, faithService, homeworkService, wellnessHomeworkService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import LapseAcknowledgmentFlow from '../components/LapseAcknowledgmentFlow';
import HomeworkCard from '../components/HomeworkCard';
import ActiveHomeworkCard from '../components/ActiveHomeworkCard';
import SOSButton from '../components/SOSButton';
import AnnouncementBanner from '../components/AnnouncementBanner';
import { Badge, Button, Card, Input } from '../components/ui';

const DASHBOARD_SECTION_STORAGE_KEY = 'client_dashboard_active_section';
const DASHBOARD_SECTIONS = [
  { key: 'focus', label: "Today's Focus" },
  { key: 'progress', label: 'Progress Snapshot' },
  { key: 'support', label: 'Support Center' },
  { key: 'homework', label: 'Homework Hub' },
];

const ClientDashboard = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [safetyFlags, setSafetyFlags] = useState(null);
  const [showLapseFlow, setShowLapseFlow] = useState(false);
  const [lapseData, setLapseData] = useState(null);
  const [showFaithConfirm, setShowFaithConfirm] = useState(false);
  const [faithEnableLoading, setFaithEnableLoading] = useState(false);
  const [assignedHomework, setAssignedHomework] = useState([]);
  const [homeworkHistoryTab, setHomeworkHistoryTab] = useState('active');
  const [homeworkLoading, setHomeworkLoading] = useState(false);
  const [showInviteClinician, setShowInviteClinician] = useState(false);
  const [inviteClinicianEmail, setInviteClinicianEmail] = useState('');
  const [inviteClinicianPhone, setInviteClinicianPhone] = useState('');
  const [inviteClinicianStatus, setInviteClinicianStatus] = useState(null);
  const [inviteClinicianLoading, setInviteClinicianLoading] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [mantra, setMantra] = useState('');
  const [mantraEditing, setMantraEditing] = useState(false);
  const [mantraDraft, setMantraDraft] = useState('');
  const [mantraSaving, setMantraSaving] = useState(false);
  const [activeDashboardSection, setActiveDashboardSection] = useState('focus');
  const [sectionAnimating, setSectionAnimating] = useState(false);
  const sectionAnimationTimerRef = useRef(null);

  const userId = user?.id || user?._id;

  const loadDashboardData = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const results = await Promise.allSettled([
        progressService.getProgress(userId),
        notificationService.refreshNotifications(),
        progressService.getSafetyFlags(userId),
        lapseService.getRecentLapse(userId),
        fetchAssignedHomework()
      ]);

      const [progressResult, notificationsResult, safetyResult, lapseResult] = results;

      if (progressResult.status === 'fulfilled') {
        setProgress(progressResult.value.data.data.progress);
        setStats(progressResult.value.data.data.completionStats);
      }

      if (notificationsResult.status === 'fulfilled') {
        setNotifications(notificationsResult.value.data.data.notifications || []);
      }

      if (safetyResult.status === 'fulfilled') {
        setSafetyFlags(safetyResult.value.data.data.flags || null);
      }

      if (lapseResult.status === 'fulfilled') {
        setLapseData(lapseResult.value.data.data || null);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // Check if client was enrolled by clinician and needs to acknowledge
    if (user && user.invitationSentBy && !user.clientEnrollmentAcknowledgement?.acknowledged) {
      navigate('/client/enrollment-acknowledgment');
      return;
    }

    if (user?.mantra) {
      setMantra(user.mantra);
    }

    loadDashboardData();
  }, [loadDashboardData, user, navigate]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    if (!installPromptEvent) return;
    const completedDays = stats?.completedDays ?? stats?.totalDays ?? 0;
    const dismissedAt = localStorage.getItem('pwa_install_prompt_dismissed_at');
    const dismissedRecently = dismissedAt && Date.now() - Number(dismissedAt) < 7 * 24 * 60 * 60 * 1000;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

    if (!isStandalone && completedDays >= 1 && !dismissedRecently) {
      setShowInstallPrompt(true);
    }
  }, [installPromptEvent, stats]);

  const fetchAssignedHomework = async () => {
    setHomeworkLoading(true);
    try {
      const response = await homeworkService.getAssignedHomework('all');
      setAssignedHomework(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching homework:', error);
    } finally {
      setHomeworkLoading(false);
    }
  };

  const [wellnessHomework, setWellnessHomework] = useState([]);
  const [wellnessHomeworkLoading, setWellnessHomeworkLoading] = useState(false);

  const fetchWellnessHomework = useCallback(async () => {
    if (!user?._id) return;
    setWellnessHomeworkLoading(true);
    try {
      const response = await wellnessHomeworkService.getActiveAssignments(user._id);
      setWellnessHomework(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching wellness homework:', error);
    } finally {
      setWellnessHomeworkLoading(false);
    }
  }, [user?._id]);

  useEffect(() => {
    fetchWellnessHomework();
  }, [fetchWellnessHomework]);

  const phaseNames = ['Stabilize', 'Understand', 'Rebuild', 'Sustain'];
  const completionRate = stats ? Math.round((stats.completedDays / user.currentDay) * 100) : 0;
  const programLength = user.programLength || 365;
  const isEligibleForGraduation = user.currentDay >= programLength && user.graduationStatus !== 'graduated';
  const currentMonth = Math.min(12, Math.ceil((user.currentDay || 1) / 30));
  const sortedAssessments = [...(progress?.monthlyAssessments || [])].sort((a, b) => a.month - b.month);
  const latestAssessment = sortedAssessments[sortedAssessments.length - 1] || null;
  const isMonthlyAssessmentDue = !latestAssessment || latestAssessment.month !== currentMonth;
  const faithEnabled = Boolean(user.faithPreferences?.faithTrackEnabled);
  const unreadNotifications = notifications.filter(notification => notification.status === 'unread');
  const showSafetyPrompt = safetyFlags?.hasConcern;
  const daysSinceLastLogin = Number.isFinite(user?.daysSinceLastLogin)
    ? user.daysSinceLastLogin
    : 0;
  const assignedClinician = user?.assignedClinician;
  const hasAssignedClinician = Boolean(assignedClinician);
  const assignedClinicianName = hasAssignedClinician && typeof assignedClinician === 'object'
    ? `${assignedClinician.title ? `${assignedClinician.title} ` : ''}${assignedClinician.firstName || ''} ${assignedClinician.lastName || ''}`.trim()
    : 'your assigned clinician';

  const activeHomework = assignedHomework.filter((item) => item.completionStatus === 'active' || item.completionStatus === 'overdue');
  const completedHomework = assignedHomework.filter((item) => item.completionStatus === 'completed');
  const archivedHomework = assignedHomework.filter((item) => item.completionStatus === 'archived');
  const homeworkTabs = {
    active: activeHomework,
    completed: completedHomework,
    archived: archivedHomework,
  };
  const visibleHomework = homeworkTabs[homeworkHistoryTab] || activeHomework;
  const activeSectionLabel = DASHBOARD_SECTIONS.find((section) => section.key === activeDashboardSection)?.label || "Today's Focus";
  const isFocusSection = activeDashboardSection === 'focus';
  const isProgressSection = activeDashboardSection === 'progress';
  const isSupportSection = activeDashboardSection === 'support';
  const isHomeworkSection = activeDashboardSection === 'homework';

  useEffect(() => {
    const persistedSection = localStorage.getItem(DASHBOARD_SECTION_STORAGE_KEY);
    if (persistedSection && DASHBOARD_SECTIONS.some((section) => section.key === persistedSection)) {
      setActiveDashboardSection(persistedSection);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(DASHBOARD_SECTION_STORAGE_KEY, activeDashboardSection);
  }, [activeDashboardSection]);

  useEffect(() => {
    return () => {
      if (sectionAnimationTimerRef.current) {
        window.clearTimeout(sectionAnimationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (location.hash === '#homework-hub' || location.hash === '#homework') {
      setActiveDashboardSection('homework');
    }
  }, [location.hash]);

  const handleSectionChange = (nextSection) => {
    if (!nextSection || nextSection === activeDashboardSection) {
      return;
    }

    setSectionAnimating(true);
    setActiveDashboardSection(nextSection);

    if (sectionAnimationTimerRef.current) {
      window.clearTimeout(sectionAnimationTimerRef.current);
    }

    sectionAnimationTimerRef.current = window.setTimeout(() => {
      setSectionAnimating(false);
      sectionAnimationTimerRef.current = null;
    }, 220);
  };

  const openHomeworkHub = () => {
    handleSectionChange('homework');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <LoadingSpinner message="Loading your dashboard..." />;
  }

  const handleMantraSave = async () => {
    setMantraSaving(true);
    try {
      await userService.updateProfile(userId, { mantra: mantraDraft.trim() });
      setMantra(mantraDraft.trim());
      setMantraEditing(false);
      updateUser({ ...user, mantra: mantraDraft.trim() });
    } catch (error) {
      console.error('Failed to save mantra:', error);
      alert('Failed to save mantra. Please try again.');
    } finally {
      setMantraSaving(false);
    }
  };

  const handleMarkNotification = async (notificationId) => {
    try {
      await notificationService.markRead(notificationId);
      setNotifications(prev => prev.map(notification => (
        notification._id === notificationId ? { ...notification, status: 'read' } : notification
      )));
    } catch (error) {
      console.error('Failed to mark notification:', error);
    }
  };

  const handleMarkAllNotifications = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(notification => ({ ...notification, status: 'read' })));
    } catch (error) {
      console.error('Failed to mark notifications:', error);
    }
  };

  const handleGraduate = async () => {
    setActionMessage(null);
    try {
      const response = await userService.graduate(user.id);
      if (response.data?.data?.user) {
        updateUser(response.data.data.user);
      }
      setActionMessage({ type: 'success', text: response.data?.message || 'Graduation complete.' });
    } catch (error) {
      setActionMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to graduate.'
      });
    }
  };

  const handleContinue = async () => {
    setActionMessage(null);
    try {
      const response = await userService.continueProgram(user.id);
      if (response.data?.data?.user) {
        updateUser(response.data.data.user);
      }
      setActionMessage({ type: 'success', text: response.data?.message || 'Program continued.' });
    } catch (error) {
      setActionMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to continue program.'
      });
    }
  };

  const handleLapseReport = async (lapsePayload) => {
    try {
      await lapseService.reportLapse(userId, lapsePayload);
      setShowLapseFlow(false);
      setActionMessage({ 
        type: 'success', 
        text: 'Your report has been saved. Remember: progress is not linear, and you are still making progress.' 
      });
      // Reload dashboard data to update everything
      loadDashboardData();
    } catch (error) {
      console.error('Failed to save lapse report:', error);
      throw error;
    }
  };

  const handleFaithCTA = () => {
    if (faithEnabled) {
      navigate(`/faith/${user.currentDay}`);
      return;
    }

    setShowFaithConfirm(true);
  };

  const handleInviteClinician = async () => {
    setInviteClinicianStatus(null);
    const email = inviteClinicianEmail.trim();
    if (!email) {
      setInviteClinicianStatus({ type: 'error', text: 'Please enter a clinician email.' });
      return;
    }

    setInviteClinicianLoading(true);
    try {
      const response = await userService.inviteClinician(userId, email, inviteClinicianPhone.trim());
      const inviteData = response.data?.data || {};
      const smsStatus = inviteData.smsStatus ? ` SMS: ${inviteData.smsStatus}.` : '';
      setInviteClinicianStatus({
        type: 'success',
        text: `Invitation sent to your clinician by email.${smsStatus}`
      });
      setInviteClinicianEmail('');
      setInviteClinicianPhone('');
    } catch (error) {
      setInviteClinicianStatus({
        type: 'error',
        text: error.response?.data?.message || 'Unable to send invitation.'
      });
    } finally {
      setInviteClinicianLoading(false);
    }
  };

  const confirmEnableFaith = async () => {
    setFaithEnableLoading(true);
    try {
      const updatedSettings = {
        faithTrackEnabled: true,
        faithTrackStartDate: new Date(),
        faithTradition: user.faithPreferences?.faithTradition || 'christian'
      };

      await faithService.updateFaithPreferences(user.id, updatedSettings);
      updateUser({
        ...user,
        faithPreferences: updatedSettings
      });
      setShowFaithConfirm(false);
      navigate(`/faith/${user.currentDay}`);
    } catch (error) {
      console.error('Failed to enable faith track:', error);
      setActionMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to enable faith track.'
      });
    } finally {
      setFaithEnableLoading(false);
    }
  };

  const handleInstallApp = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    setInstallPromptEvent(null);
    setShowInstallPrompt(false);
    if (choice?.outcome !== 'accepted') {
      localStorage.setItem('pwa_install_prompt_dismissed_at', String(Date.now()));
    }
  };

  const handleDismissInstallPrompt = () => {
    localStorage.setItem('pwa_install_prompt_dismissed_at', String(Date.now()));
    setShowInstallPrompt(false);
    setInstallPromptEvent(null);
  };

  return (
    <>
      {showLapseFlow && (
        <LapseAcknowledgmentFlow
          userId={userId}
          currentDay={user.currentDay}
          onClose={() => setShowLapseFlow(false)}
          onComplete={handleLapseReport}
        />
      )}

      {showFaithConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 px-4">
          <Card className="w-full max-w-lg">
            <h3 className="text-xl font-display font-semibold text-neutral-900">
              Add the Faith Pillar?
            </h3>
            <p className="mt-2 text-sm text-neutral-600">
              This will add scripture-based devotionals and faith-focused reflection prompts to your daily plan.
              You can turn it off anytime in Settings.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button variant="secondary" onClick={() => setShowFaithConfirm(false)}>
                Not right now
              </Button>
              <Button onClick={confirmEnableFaith} disabled={faithEnableLoading}>
                {faithEnableLoading ? 'Enabling...' : 'Yes, add faith'}
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      <Navigation />
      <main className="mx-auto max-w-6xl px-6 py-10 pb-24 md:pb-10">
        {/* System Announcements */}
        <AnnouncementBanner />
        
        <div className="mb-6">
          <Badge variant={`phase.${phaseNames[user.currentPhase - 1]?.toLowerCase()}`} size="sm">
            Phase {user.currentPhase}: {phaseNames[user.currentPhase - 1]}
          </Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            Welcome back, {user.firstName}!
          </h2>
          <p className="mt-2 text-neutral-600">
            Day {user.currentDay} of 365 • Keep your momentum moving forward.
          </p>
          <p className="mt-1 text-sm text-neutral-500">
            {daysSinceLastLogin > 0
              ? `${daysSinceLastLogin} day${daysSinceLastLogin === 1 ? '' : 's'} since your last login.`
              : 'Last login: today.'}
          </p>

          <Card className="mt-5 border-l-4 border-l-primary-500 bg-primary-50/70">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Quick Help Access</p>
                <p className="mt-1 text-lg font-semibold text-neutral-900">Homework Hub</p>
                <p className="text-sm text-neutral-700">
                  If support is needed right now, start here for guided exercises and practical coping help.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary" onClick={openHomeworkHub}>
                  Open Homework Hub
                </Button>
                <Button variant="secondary" onClick={() => navigate('/homework-resources')}>
                  Homework Resources
                </Button>
              </div>
            </div>
          </Card>

          <div className="sticky top-20 z-20 mt-6 rounded-2xl border border-neutral-200 bg-white/95 px-4 py-4 shadow-soft backdrop-blur hidden md:flex">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Dashboard View</p>
                <p className="text-sm text-neutral-700">{activeSectionLabel}</p>
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                <select
                  value={activeDashboardSection}
                  onChange={(event) => handleSectionChange(event.target.value)}
                  className="min-w-[220px] rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  aria-label="Select dashboard section"
                >
                  {DASHBOARD_SECTIONS.map((section) => (
                    <option key={section.key} value={section.key}>{section.label}</option>
                  ))}
                </select>
                <div className="hidden flex-wrap gap-2 lg:flex">
                  {DASHBOARD_SECTIONS.map((section) => (
                    <Button
                      key={`dashboard-nav-${section.key}`}
                      size="sm"
                      variant={activeDashboardSection === section.key ? 'primary' : 'ghost'}
                      onClick={() => handleSectionChange(section.key)}
                    >
                      {section.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mt-6 overflow-hidden rounded-[30px] border border-neutral-200/90 bg-gradient-to-b from-white to-neutral-50 shadow-soft-lg backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-primary-100/50 blur-3xl" />
          <div className="pointer-events-none absolute -left-12 bottom-0 h-32 w-32 rounded-full bg-cyan-100/40 blur-2xl" />
          <div
            className={`relative p-5 transition-all duration-200 md:p-8 ${sectionAnimating ? 'translate-y-1 opacity-0' : 'translate-y-0 opacity-100'}`}
          >

        {isFocusSection && showInstallPrompt && (
          <Card className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm text-neutral-500">Install MHDF Momentum</p>
              <p className="mt-1 text-lg font-semibold text-neutral-900">
                Add the app to your home screen
              </p>
              <p className="text-sm text-neutral-600">
                Faster access, offline-ready resources, and a focused full-screen experience.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="secondary" onClick={handleDismissInstallPrompt}>
                Not now
              </Button>
              <Button variant="primary" onClick={handleInstallApp}>
                Install
              </Button>
            </div>
          </Card>
        )}

        {isSupportSection && (
        <Card className="mb-6 border border-amber-200 bg-amber-50">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Important Notice</p>
              <p className="mt-2 text-sm text-amber-900">
                This notification system is NOT a substitute for contacting emergency services.
              </p>
            </div>
            <div className="text-sm text-amber-900">
              <p className="font-semibold">If you are experiencing a mental health emergency, substance abuse crisis, or are in immediate danger:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>Call 911 immediately for life-threatening emergencies</li>
                <li>Call 988 (Suicide & Crisis Lifeline) for mental health crises</li>
                <li>Text "HELLO" to 741741 (Crisis Text Line) for text support</li>
                <li>Contact your treatment provider, therapist, or clinician directly</li>
                <li>Go to your nearest emergency room</li>
              </ul>
            </div>
            <p className="text-xs text-amber-800">
              The MHDF Momentum alert system is designed to support ongoing wellness monitoring and provide supplemental notifications to designated contacts. It operates with delays and is not monitored 24/7. Always contact authorities or emergency services directly for immediate assistance.
            </p>
          </div>
        </Card>
        )}

        {isProgressSection && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Current Streak</p>
              <p className="mt-2 text-3xl font-semibold text-neutral-900">{progress?.currentStreak || 0}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <Flame className="h-6 w-6" />
            </div>
          </Card>

          <Card className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Days Completed</p>
              <p className="mt-2 text-3xl font-semibold text-neutral-900">{stats?.totalDays || 0}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-100 text-success-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
          </Card>

          <Card colorVariant="cyan" className="flex items-center justify-between">
            <div>
              <p className="text-sm text-cyan-600 font-medium">Completion Rate</p>
              <p className="mt-2 text-3xl font-bold text-cyan-700">{completionRate}%</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-100 text-cyan-600">
              <TrendingUp className="h-6 w-6" />
            </div>
          </Card>

          <Card colorVariant="indigo" className="flex items-center justify-between">
            <div>
              <p className="text-sm text-indigo-600 font-medium">Current Phase</p>
              <p className="mt-2 text-3xl font-bold text-indigo-700">{user.currentPhase}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
              <Calendar className="h-6 w-6" />
            </div>
          </Card>
        </div>
        )}

        {/* Personal Mantra */}
        {isProgressSection && (
        <Card className="mt-6" style={{ borderLeft: '3px solid #1D9E75' }}>
          {mantra && !mantraEditing ? (
            <div>
              <div className="flex items-start justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3">
                  My Daily Mantra
                </p>
                <button
                  onClick={() => { setMantraDraft(mantra); setMantraEditing(true); }}
                  className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  Edit
                </button>
              </div>
              <p className="text-lg leading-relaxed text-neutral-800" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
                "{mantra}"
              </p>
            </div>
          ) : mantraEditing ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3">
                {mantra ? 'Edit Your Mantra' : 'Create Your Mantra'}
              </p>
              <textarea
                value={mantraDraft}
                onChange={(e) => setMantraDraft(e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full rounded-lg border border-neutral-300 px-4 py-3 text-neutral-800 placeholder-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none resize-none"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 15, lineHeight: 1.6 }}
                placeholder="Write a personal mantra or affirmation that speaks to you..."
                autoFocus
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-neutral-400">{mantraDraft.length}/500</span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setMantraEditing(false); setMantraDraft(''); }}
                    disabled={mantraSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleMantraSave}
                    disabled={mantraSaving || !mantraDraft.trim()}
                  >
                    {mantraSaving ? 'Saving...' : 'Save Mantra'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-700">Set a personal mantra</p>
                <p className="mt-1 text-sm text-neutral-500">
                  Create a daily affirmation or intention to guide your wellness journey.
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => { setMantraDraft(''); setMantraEditing(true); }}
              >
                Create Mantra
              </Button>
            </div>
          )}
        </Card>
        )}

        {isSupportSection && (
        <Card colorVariant="indigo" borderAccent className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm text-neutral-500">Encouragement Messages</p>
            <p className="mt-1 text-lg font-semibold text-neutral-900">
              {hasAssignedClinician
                ? `You are an active client of ${assignedClinicianName}`
                : 'Check notes from your clinician'}
            </p>
            <p className="text-sm text-neutral-600">
              {hasAssignedClinician
                ? 'No invite is needed. To change clinician access, update it in User Settings.'
                : 'One-way encouragement based on your progress.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {hasAssignedClinician ? (
              <Button variant="secondary" onClick={() => navigate('/settings')}>
                User Settings
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setShowInviteClinician(true)}>
                Invite Clinician
              </Button>
            )}
            <Button variant="primary" onClick={() => navigate('/messages')}>
              View Messages
            </Button>
          </div>
        </Card>
        )}

        {isSupportSection && showInviteClinician && !hasAssignedClinician && (
          <Card className="mt-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-display font-semibold text-neutral-900">Invite Your Clinician</h3>
                <p className="text-sm text-neutral-600">
                  If your clinician already uses MHDF Momentum, send them an invite to oversee your account.
                </p>
              </div>
              <Button variant="ghost" onClick={() => setShowInviteClinician(false)}>Close</Button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Input
                label="Clinician email"
                type="email"
                value={inviteClinicianEmail}
                onChange={(event) => setInviteClinicianEmail(event.target.value)}
                fullWidth
              />
              <Input
                label="Clinician phone (optional, for SMS invite)"
                type="tel"
                value={inviteClinicianPhone}
                onChange={(event) => setInviteClinicianPhone(event.target.value)}
                fullWidth
              />
            </div>

            {inviteClinicianStatus && (
              <div
                className={`mt-4 rounded-soft border p-3 text-sm ${
                  inviteClinicianStatus.type === 'success'
                    ? 'border-success-200 bg-success-50 text-success-700'
                    : 'border-error-200 bg-error-50 text-error-700'
                }`}
              >
                {inviteClinicianStatus.text}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-3">
              <Button variant="secondary" onClick={() => setShowInviteClinician(false)}>
                Cancel
              </Button>
              <Button onClick={handleInviteClinician} disabled={inviteClinicianLoading}>
                {inviteClinicianLoading ? 'Sending...' : 'Send Invite'}
              </Button>
            </div>
          </Card>
        )}

        {isProgressSection && stats && (
          <Card className="mt-6">
            <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Pillar Completions</h3>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="text-center p-4 rounded-soft bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 hover:shadow-md transition-all">
                <p className="text-sm text-purple-600 mb-1 font-medium">Mind</p>
                <p className="text-3xl font-bold text-purple-700">{stats.mindCompletions || 0}</p>
              </div>
              <div className="text-center p-4 rounded-soft bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 hover:shadow-md transition-all">
                <p className="text-sm text-emerald-600 mb-1 font-medium">Move</p>
                <p className="text-3xl font-bold text-emerald-700">{stats.moveCompletions || 0}</p>
              </div>
              <div className="text-center p-4 rounded-soft bg-gradient-to-br from-coral-50 to-coral-100/50 border border-coral-200 hover:shadow-md transition-all">
                <p className="text-sm text-coral-600 mb-1 font-medium">Journal</p>
                <p className="text-3xl font-bold text-coral-700">{stats.journalCompletions || 0}</p>
              </div>
              <div className={`text-center p-4 rounded-soft transition-all border ${faithEnabled ? 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200 hover:shadow-md' : 'bg-neutral-50 border-neutral-200'}`}>
                <p className={`text-sm mb-1 font-medium ${faithEnabled ? 'text-amber-600' : 'text-neutral-500'}`}>Faith</p>
                <p className={`text-3xl font-bold ${faithEnabled ? 'text-amber-700' : 'text-neutral-400'}`}>
                  {faithEnabled ? (stats.faithCompletions || 0) : '—'}
                </p>
                {!faithEnabled && (
                  <p className="mt-1 text-xs text-neutral-500">Enable in Settings</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {isSupportSection && lapseData?.inAdaptiveWindow && (
          <Card colorVariant="cyan" borderAccent className="mt-8">
            <h3 className="text-lg font-display font-semibold text-cyan-900">You're in Reground Mode</h3>
            <p className="mt-1 text-sm font-semibold text-teal-700">
              {(() => {
                const remaining = Math.max(0, 7 - (lapseData?.daysSinceReport || 0));
                return remaining === 0
                  ? 'You will exit Reground Mode tomorrow.'
                  : `${remaining} day${remaining === 1 ? '' : 's'} remaining in Reground Mode.`;
              })()}
            </p>
            <p className="mt-2 text-sm text-gray-700">
              Progress isn't a straight line. The next few days of content will focus on grounding, 
              self-compassion, and immediate stabilization. You're doing the work by being here.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button variant="secondary" onClick={() => navigate('/safety-plan')}>
                View Safety Plan
              </Button>
              <Button variant="ghost" onClick={() => navigate('/progress')}>
                Review Your Progress
              </Button>
            </div>
          </Card>
        )}

        {isSupportSection && !lapseData?.inAdaptiveWindow && (
          <Card className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-display font-semibold text-neutral-800">Need Support?</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  If you're having a difficult day or need immediate support, we're here without judgment.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setShowLapseFlow(true)}>
                  Acknowledge a Setback
                </Button>
                <Button variant="primary" onClick={openHomeworkHub}>
                  Open Homework Hub
                </Button>
                <Button variant="ghost" onClick={() => navigate('/client-resources')}>
                  Coping Resources
                </Button>
              </div>
            </div>
          </Card>
        )}

        {isSupportSection && (unreadNotifications.length > 0 || showSafetyPrompt) && (
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {unreadNotifications.length > 0 && (
              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-display font-semibold text-neutral-800">Notifications</h3>
                    <p className="mt-1 text-sm text-neutral-500">Keep up with reminders and check-ins.</p>
                  </div>
                  <Button size="sm" variant="secondary" onClick={handleMarkAllNotifications}>
                    Mark all read
                  </Button>
                </div>
                <div className="mt-4 space-y-3">
                  {unreadNotifications.map(notification => (
                    <div key={notification._id} className="rounded-soft border border-neutral-200 bg-white p-3">
                      <p className="text-sm font-semibold text-neutral-800">{notification.title}</p>
                      <p className="mt-1 text-sm text-neutral-600">{notification.message}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-2"
                        onClick={() => handleMarkNotification(notification._id)}
                      >
                        Mark read
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {showSafetyPrompt && (
              <Card className="border-l-4 border-l-warning-500">
                <h3 className="text-lg font-display font-semibold text-neutral-800">Safety Check-In</h3>
                <p className="mt-2 text-sm text-neutral-600">
                  It looks like the last few days have been heavy. You are not alone, and support is available.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button variant="secondary" onClick={() => navigate('/safety-plan')}>
                    Open Safety Plan
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/client-resources')}>
                    Coping Resources
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}

        {isFocusSection && isMonthlyAssessmentDue && (
          <Card className="mt-8 border-l-4 border-l-blue-500 bg-blue-50">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-display font-semibold text-neutral-900">
                  Complete today’s assessment first
                </h3>
                <p className="mt-1 text-sm text-neutral-700">
                  Your monthly check-in keeps the program tailored to your current needs.
                </p>
              </div>
              <Button onClick={() => navigate('/progress')}>
                Complete Assessment
              </Button>
            </div>
          </Card>
        )}

        {isProgressSection && (isEligibleForGraduation || user.graduationStatus === 'graduated') && (
          <Card className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-800">
                  Graduation Milestone
                </h3>
                <p className="mt-2 text-sm text-neutral-500">
                  You’ve completed {programLength} days. Celebrate your progress and continue if you want.
                </p>
              </div>
              {user.certificateUrl && (
                <Button variant="secondary" onClick={() => window.open(user.certificateUrl, '_blank')}>
                  View Certificate
                </Button>
              )}
            </div>

            {actionMessage && (
              <div
                className={`mt-4 rounded-soft border p-3 text-sm ${
                  actionMessage.type === 'success'
                    ? 'border-success-200 bg-success-50 text-success-700'
                    : 'border-error-200 bg-error-50 text-error-700'
                }`}
              >
                {actionMessage.text}
              </div>
            )}

            {user.graduationStatus !== 'graduated' ? (
              <div className="mt-4 flex flex-wrap gap-3">
                <Button onClick={handleGraduate}>Graduate & Generate Certificate</Button>
                <Button variant="secondary" onClick={handleContinue}>Continue 365 More Days</Button>
              </div>
            ) : (
              <p className="mt-4 text-sm text-neutral-600">
                You’re officially graduated. Continue anytime to start another 365-day cycle.
              </p>
            )}
          </Card>
        )}

        {isProgressSection && isMonthlyAssessmentDue && (
          <Card className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-800">
                  Monthly Milestone Check-In
                </h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Month {currentMonth} assessment is ready. Track your progress across five domains.
                </p>
              </div>
              <Button variant="secondary" onClick={() => navigate('/progress')}>
                Complete Assessment
              </Button>
            </div>
          </Card>
        )}

        {isFocusSection && (
        <Card className="mt-8" hover>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-display font-semibold text-neutral-800">
                Today's Activities • Day {user.currentDay}
              </h3>
              <p className="mt-2 text-sm text-neutral-500">
                Complete your {user.faithPreferences?.faithTrackEnabled ? 'four' : 'three'} daily pillars.
              </p>
            </div>
            <Badge variant="secondary">{completionRate}% Complete</Badge>
          </div>

          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all"
              style={{ width: `${completionRate}%` }}
            />
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-4">
            <Button variant="purple" className="w-full justify-center" onClick={() => navigate(`/daily/${user.currentDay}`)}>
              <BookOpen className="h-4 w-4" />
              Mind Exercise
            </Button>
            <Button variant="emerald" className="w-full justify-center" onClick={() => navigate(`/daily/${user.currentDay}`)}>
              <Activity className="h-4 w-4" />
              Movement Activity
            </Button>
            <Button variant="coral" className="w-full justify-center" onClick={() => navigate(`/daily/${user.currentDay}`)}>
              <BookOpen className="h-4 w-4" />
              Journal Prompt
            </Button>
            <Button
              className="w-full justify-center"
              variant={faithEnabled ? 'amber' : 'secondary'}
              onClick={handleFaithCTA}
            >
              <Cross className="h-4 w-4" />
              {faithEnabled ? 'Faith Pillar (Optional)' : 'Enable Faith Pillar'}
            </Button>
          </div>
        </Card>
        )}

        {isFocusSection && (
          <Card className="mt-6 border border-primary-200 bg-gradient-to-r from-primary-50 to-cyan-50">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-display font-semibold text-neutral-900">Need help with what you feel today?</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  Jump to Homework Hub for focused support exercises.
                </p>
              </div>
              <Button variant="primary" onClick={openHomeworkHub}>
                Go to Homework Hub
              </Button>
            </div>
          </Card>
        )}

        {isProgressSection && (
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-lg font-display font-semibold text-neutral-800">Quick Actions</h3>
            <div className="mt-4 flex flex-col gap-3">
              <Button variant="outline" onClick={() => navigate('/journal')}>
                View Journal History
              </Button>
              <Button variant="outline" onClick={() => navigate('/progress')}>
                View Progress & Trends
              </Button>
              <Button variant="outline" onClick={() => navigate('/safety-plan')}>
                Update Safety Plan
              </Button>
              <Button variant="outline" onClick={() => navigate('/homework-resources')}>
                📚 Homework Library
              </Button>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-display font-semibold text-neutral-800">Recent Badges</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {progress?.badges?.slice(-5).map((badge, index) => (
                <Badge key={index} variant="primary">
                  {badge.icon} {badge.name}
                </Badge>
              ))}
              {(!progress?.badges || progress.badges.length === 0) && (
                <p className="text-sm text-neutral-500">Complete daily activities to earn badges!</p>
              )}
            </div>
          </Card>
        </div>
        )}

        {/* Wellness-Area Homework — condition-based library */}
        {isHomeworkSection && (
        <Card className="mt-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-display font-semibold text-neutral-800">
                  Your Homework
                </h3>
                {user?.wellnessAreasSetBy === 'clinician' && (
                  <Badge variant="primary">Curated by your care team</Badge>
                )}
              </div>
              <p className="mt-2 text-sm text-neutral-500">
                Matched to the areas you are working on, with room for clinician additions.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/homework-library')}
            >
              Browse Homework Library
            </Button>
          </div>

          {(!user?.wellnessAreas || user.wellnessAreas.length === 0) && (
            <Card variant="feature" padding="sm" className="mt-4">
              <p className="text-sm text-neutral-700">
                Set your focus areas to get homework matched to your needs.
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => navigate('/profile')}
              >
                Set focus areas
              </Button>
            </Card>
          )}

          <div className="mt-4 space-y-4">
            {wellnessHomeworkLoading && (
              <p className="text-sm text-neutral-500">Loading your homework...</p>
            )}
            {!wellnessHomeworkLoading && wellnessHomework.length === 0 && (
              <p className="text-sm text-neutral-500">
                No active exercises yet. Browse the library to add your first one.
              </p>
            )}
            {!wellnessHomeworkLoading && wellnessHomework.map((assignment) => (
              <ActiveHomeworkCard
                key={assignment._id}
                assignment={assignment}
                onChange={fetchWellnessHomework}
              />
            ))}
          </div>
        </Card>
        )}

        {/* Homework Section */}
        {isHomeworkSection && assignedHomework.length > 0 && (
          <Card className="mt-8">
            <h3 className="text-xl font-display font-semibold text-neutral-800">
              📋 Homework History from Your Therapist
            </h3>
            <p className="mt-2 text-sm text-neutral-500">
              Review active, completed, and archived assignments as part of your treatment plan.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={homeworkHistoryTab === 'active' ? 'primary' : 'ghost'}
                onClick={() => setHomeworkHistoryTab('active')}
              >
                Active ({activeHomework.length})
              </Button>
              <Button
                size="sm"
                variant={homeworkHistoryTab === 'completed' ? 'primary' : 'ghost'}
                onClick={() => setHomeworkHistoryTab('completed')}
              >
                Completed ({completedHomework.length})
              </Button>
              <Button
                size="sm"
                variant={homeworkHistoryTab === 'archived' ? 'primary' : 'ghost'}
                onClick={() => setHomeworkHistoryTab('archived')}
              >
                Archived ({archivedHomework.length})
              </Button>
            </div>

            {homeworkLoading ? (
              <p className="mt-4 text-sm text-neutral-500">Loading assignments...</p>
            ) : (
              <div className="mt-4 space-y-4">
                {visibleHomework.length === 0 && (
                  <p className="text-sm text-neutral-500">
                    No {homeworkHistoryTab} assignments yet.
                  </p>
                )}
                {visibleHomework.map((hw) => (
                  <HomeworkCard
                    key={hw._id}
                    homework={hw}
                  />
                ))}
              </div>
            )}
          </Card>
        )}
          </div>
        </div>
      </main>

      <SOSButton variant="floating" />

      {/* Mobile Bottom Tab Bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-neutral-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
        aria-label="Dashboard sections"
      >
        <div className="flex">
          {[
            { key: 'homework', label: 'Homework', Icon: BookOpen },
            { key: 'focus', label: "Today", Icon: Calendar },
            { key: 'progress', label: 'Progress', Icon: TrendingUp },
            { key: 'support', label: 'Support', Icon: Activity },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleSectionChange(key)}
              className={`flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-semibold tracking-wide transition-colors ${
                activeDashboardSection === key
                  ? 'text-primary-600 border-t-2 border-primary-600 -mt-px'
                  : 'text-neutral-400 border-t-2 border-transparent -mt-px'
              }`}
              aria-label={label}
              aria-current={activeDashboardSection === key ? 'page' : undefined}
            >
              <Icon size={20} />
              {label}
            </button>
          ))}
        </div>
      </nav>
    </>
  );
};

export default ClientDashboard;
